// askSmart-ui/src/pages/TOSP/portal/TospPortal.tsx
import React, { useEffect, useState } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { TospDataProvider, useTospData } from "../TospDataContext";
import { tenancyEndDate, tenantLabel, tenantOpeningDate, tenantReportsSales } from "../types";
import "./TospPortal.css";
import ExpenseStatement from "./ExpenseStatement";
import LeaseSummaryScreen from "./LeaseSummaryScreen";
import SalesHistory from "./SalesHistory";
import TenantDashboard from "./TenantDashboard";
import DelegatesScreen from "./DelegatesScreen";
import PortalMenu from "./PortalMenu";
import {
  isPrimaryContact,
  SALES_DUE_DAY,
  tenantsForEmail,
  YearMonth,
} from "./portalSummary";
import SalesEntryModal from "./SalesEntryModal";
import { monthsOwing, useStoredSales } from "./tenantSales";
import { useProfileComplete } from "./useProfileComplete";

/** Which screen the portal is showing. The dashboard is the landing page. */
export type PortalView = "dashboard" | "sales" | "lease" | "expenses" | "delegates";

/** Where the chosen business is remembered between visits. */
const SELECTED_TENANT_KEY = "tosp:selectedTenant";

// Tenant side of TOSP (/tosp). Reads the same records the admin module writes,
// filtered to the one tenant signed in — it has no writes of its own yet.
export default function TospPortal() {
  return (
    <TospDataProvider>
      <TospPortalContent />
    </TospDataProvider>
  );
}

function TospPortalContent() {
  const { ready, error, tenants, unitById, configuration } = useTospData();
  const { user } = useAuth();
  const userId = (user as { userID?: string } | null | undefined)?.userID;
  const profileComplete = useProfileComplete(userId);
  const [view, setView] = useState<PortalView>("dashboard");
  /** Set when the tenant closes the overdue prompt — for this visit only. */
  const [dismissedOverdue, setDismissedOverdue] = useState(false);

  // One store for both screens, so the dashboard and the Sales list can't
  // hold diverging copies of what's been reported.
  const { sales, submit } = useStoredSales();

  // Every business this account holds. One is the ordinary case; several
  // happens when somebody runs two shops in the building, and routinely
  // during onboarding while the property manager holds each new tenancy
  // until its tenant has an account.
  const myTenants = tenantsForEmail(tenants, user?.email, user?.userID);

  /** Which business is on screen. Remembered, so a visit resumes where it left. */
  const [selectedId, setSelectedId] = useState<string>(
    () => window.localStorage.getItem(SELECTED_TENANT_KEY) || "",
  );

  // Falls back to the first rather than to nothing: a remembered id can name a
  // tenancy that has since been handed on, and an empty screen would read as
  // "you have no tenancy" when they plainly do.
  const tenant =
    myTenants.find((candidate) => candidate.id === selectedId) || myTenants[0] || null;

  useEffect(() => {
    if (tenant) window.localStorage.setItem(SELECTED_TENANT_KEY, tenant.id);
  }, [tenant]);

  // No seeding here. The supplied history belongs to one tenant; laying it
  // down for whoever opens the portal filed that tenant's trading figures as
  // the next tenant's own submissions. See SEED_SALES in tenantSales.ts.

  if (!ready) {
    return (
      <div className="tosp-portal">
        <div className="tosp-portal-notice">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tosp-portal">
        <div className="tosp-portal-notice tosp-portal-notice-error">{error}</div>
      </div>
    );
  }

  // Profile before anything else, the same order SMRT Docs uses. It applies to
  // delegates as much as to primary contacts: a delegate reports the tenant's
  // sales under their own name, so the same record has to exist for them.
  //
  // Only gates once there is a user id to check. Without one the answer never
  // arrives, and hanging on "Checking…" would be worse than the not-linked
  // notice below, which is where an unidentified caller already ends up.
  if (userId) {
    if (profileComplete === null) {
      return (
        <div className="tosp-portal">
          <div className="tosp-portal-notice">Checking your profile…</div>
        </div>
      );
    }

    if (!profileComplete) {
      return (
        <div className="tosp-portal">
          <div className="tosp-portal-notice">
            <strong>Please complete your profile first.</strong>
            <p>
              Your profile has to be filled in and submitted before the tenant portal
              opens. Open <a href="/profiles">your profile</a> to finish it.
            </p>
            <p>
              If it says no template has been assigned, the property manager has to
              assign one before you can do anything — ask them to set one up for{" "}
              <span className="tosp-portal-email">{user?.email || "your account"}</span>.
            </p>
          </div>
        </div>
      );
    }
  }

  // Signed in, but the account isn't linked to a tenant — the contact email on
  // every tenant record is something else. Nothing the tenant can fix
  // themselves, so it points at the people who can.
  if (!tenant) {
    return (
      <div className="tosp-portal">
        <div className="tosp-portal-notice">
          <strong>Your account isn't linked to a tenant yet.</strong>
          <p>
            Ask the property manager to add{" "}
            <span className="tosp-portal-email">{user?.email || "your email address"}</span>{" "}
            as the primary contact on your tenant record.
          </p>
        </div>
      </div>
    );
  }

  /** The primary contact may appoint delegates; a delegate may not. */
  const primary = isPrimaryContact(tenant, user?.email);

  const record = (target: YearMonth, grossRevenue: number) =>
    submit(tenant.id, target, grossRevenue, user?.email || "");

  // The oldest month still owing. Recomputed from the records, so entering it
  // hands the modal straight to the next one — a tenant three months behind
  // clears them oldest first without going looking for them.
  const owing = monthsOwing(sales, tenant.id, new Date(), SALES_DUE_DAY, {
    openedOn: tenantOpeningDate(tenant),
    closedOn: tenancyEndDate(tenant),
    reportsSales: tenantReportsSales(tenant),
  })[0];

  // Which of the other businesses have months outstanding.
  //
  // Only the business on screen is prompted — being handed two queues at once
  // is a trap, not a reminder. But reporting is a lease obligation, so a
  // business that owes months can't simply be invisible until they happen to
  // switch to it. The switcher marks them instead.
  const owingElsewhere = new Set(
    myTenants
      .filter(
        (candidate) =>
          candidate.id !== tenant.id &&
          monthsOwing(sales, candidate.id, new Date(), SALES_DUE_DAY, {
        openedOn: tenantOpeningDate(candidate),
        closedOn: tenancyEndDate(candidate),
        reportsSales: tenantReportsSales(candidate),
      }).length > 0,
      )
      .map((candidate) => candidate.id),
  );

  const screen =
    view === "sales" ? (
      <SalesHistory
        tenant={tenant}
        sales={sales}
        onSubmitSales={record}
        onBack={() => setView("dashboard")}
      />
    ) : view === "delegates" && primary ? (
      <DelegatesScreen
        // Every business this account is PRIMARY for — not the ones it's
        // merely been delegated. Delegating on somebody else's behalf is
        // exactly what a delegate may not do.
        tenancies={myTenants.filter((candidate) =>
          isPrimaryContact(candidate, user?.email),
        )}
        onBack={() => setView("dashboard")}
      />
    ) : view === "lease" ? (
      <LeaseSummaryScreen
        tenant={tenant}
        unitById={unitById}
        onBack={() => setView("dashboard")}
      />
    ) : view === "expenses" ? (
      <ExpenseStatement
        tenant={tenant}
        sales={sales}
        onBack={() => setView("dashboard")}
      />
    ) : (
      <TenantDashboard
        // Keyed on the business, so switching to one is an arrival rather than
        // a re-render. The tiles count up to the new figures the way they do
        // on a first visit, and the workings panel opened against the previous
        // business closes rather than staying open over somebody else's
        // numbers.
        key={tenant.id}
        tenant={tenant}
        tenants={myTenants}
        onSelectTenant={(id) => {
          setSelectedId(id);
          // A month declined for one business shouldn't stay declined for the
          // next — the prompt is about the business, not the visit.
          setDismissedOverdue(false);
        }}
        owingElsewhere={owingElsewhere}
        unitById={unitById}
        configuration={configuration}
        onNavigate={setView}
        sales={sales}
        onSubmitSales={record}
      />
    );

  return (
    <>
      {/* One menu for the whole portal, rendered above whichever screen is
          showing. Kept here rather than in each screen so it can't drift
          between them, and so a tenant on the Sales sheet can reach
          Delegates without going back to the dashboard first. */}
      <PortalMenu
        isPrimary={primary}
        onNavigate={setView}
        wide={view === "sales"}
      />
      {screen}
      {owing && !dismissedOverdue && (
        <SalesEntryModal
          // Keyed on the month so moving to the next one starts a clean form
          // rather than carrying the last figure into it.
          key={`${owing.year}-${owing.month}`}
          target={{ year: owing.year, month: owing.month }}
          dueOn={owing.dueOn}
          // Named on the modal, not just implied by what's behind it. A figure
          // reported against the wrong business is only correctable by an
          // admin.
          tenantName={myTenants.length > 1 ? tenantLabel(tenant) : ""}
          // Closing gives up on the whole queue for this visit, not just this
          // month — being handed the next overdue month after declining one
          // would be a trap rather than a prompt.
          onClose={() => setDismissedOverdue(true)}
          onSubmit={(grossRevenue) =>
            record({ year: owing.year, month: owing.month }, grossRevenue)
          }
        />
      )}
    </>
  );
}
